import { MATERIAL_OPTIONS } from '@common/constants/MaterialOptions';
import { roundToArrowLengthGranularity } from '@common/helpers/math-helper';
import { CornerInfo, DrawingEdge, DrawingShape, MaterialModel, SkeletonSeg } from '@common/models';

import drawingTool from '../../pages/easy-estimator/components/scrollable-area/design-countertop/drawer/drawingTool';

type SegmentCorners = CornerInfo[];
const emptyArray = [] satisfies CornerInfo[] | SegmentCorners[];

/**
 * @param base the length of the side, not including radius/bump outs
 * @param extras the depth of the radius/bump out that extends this edge
 */
interface ComponentLength {
    base: number;
    extras: number;
}

interface ShapeSegment {
    skeletonIndex: number;
    bone: SkeletonSeg;
    corners: CornerInfo[];
    edges: DrawingEdge[];
    dimensions: number[];
    componentLengths: ComponentLength[];
}

const emptyDimension: ComponentLength = { base: 0, extras: 0 };

/**
 * Build a list of the _drawing_ edges of the shape matching the corners array
 * @param segmentCorners array of corners in contiguous order of the polygon defining a segment of the shape
 * @param shape the shape
 * @returns an array off DrawingEdges _or undefined_ values if a polygon edge isn't drawn
 */
const makeEdgesFromSegmentCorners = (segmentCorners: CornerInfo[], shape: DrawingShape) =>
    segmentCorners.map((start, startIndex, corners) => {
        const endIndex = startIndex < corners.length - 1 ? startIndex + 1 : 0; //wrap around at the end
        const end = corners[endIndex];
        return shape.drawingEdges.find(
            (edge) =>
                (edge.startCornerId === start.id && edge.endCornerId === end.id) ||
                (edge.startCornerId === end.id && edge.endCornerId === start.id)
        );
    });

/**
 * Compute the lengths of all the segment edges, using the depth of the skeleton bone for non-drawn edges
 * @param edges array of edges of the polygon, or undefined if the edge is not drawn
 * @param bone the skeleton bone this segment was derived from (to get the depth)
 * @param shape the original shape
 * @returns an array of ComponentLength matching the original edges array
 * @see ComponentLength
 */
const getLengthsFromEdges = (
    edges: (DrawingEdge | undefined)[],
    bone: SkeletonSeg,
    shape: DrawingShape
): ComponentLength[] =>
    edges.map((edge) => {
        if (!edge) {
            return { base: bone.depth * 2, extras: 0 };
        }
        const arrows = shape.arrowsInfo?.filter((arrow) => arrow.drawingEdgeId === edge.id && !arrow.isDiagonalCorner);
        if (!arrows?.length) {
            return emptyDimension;
        }
        const baseArrow = arrows.find((arrow) => !arrow.isInterior && !arrow.isBumpOut && !arrow.isRadiusOut);
        const extrasArrow = arrows.find((arrow) => arrow.isBumpOut || arrow.isRadiusOut);
        return { base: baseArrow?.size ?? 0, extras: extrasArrow?.size ?? 0 }; // base, extras that get added to adjacents
    });

/**
 * build a list of the details of the ShapeSegment that make up the shape
 * @param shape original shape
 */
export const getSegmentsFromShape = (shape: DrawingShape): ShapeSegment[] => {
    if (!shape || shape.skeleton.length === 0) {
        return emptyArray;
    }
    const corners = Object.values(shape.cornerMap);
    return shape.skeleton
        .map((bone, segmentIndex) => {
            const {
                prevUpperOutlinePt: a,
                nextUpperOutlinePt: b,
                prevBottomOutlinePt: c,
                nextBottomOutlinePt: d,
            } = drawingTool.getOutlinePointsFromCorners(corners, segmentIndex + 1);
            if (a && b && c && d) {
                const segmentCorners = [a, b, d, c] as SegmentCorners; // *** REARRANGE THE ORDER INTENTIONALLY!! ***
                const edges = makeEdgesFromSegmentCorners(segmentCorners, shape);
                const componentLengths = getLengthsFromEdges(edges, bone, shape);

                // add the "extras" from any given side to the lengths of its adjacent sides
                // If there are sides A,B,C,D and B has a bump out, that adds to sides A and C
                const dimensions = componentLengths.map((dim, index, array) => {
                    const prevIndex = index > 0 ? index - 1 : array.length - 1;
                    const nextIndex = index < array.length - 1 ? index + 1 : 0;
                    return dim.base + array[prevIndex].extras + array[nextIndex].extras;
                });
                return {
                    skeletonIndex: segmentIndex,
                    bone,
                    corners: segmentCorners,
                    edges,
                    dimensions,
                    componentLengths,
                } as ShapeSegment;
            }
            return undefined;
        })
        .filter((corners) => !!corners) as ShapeSegment[]; // remove any with any undefined result
};

/**
 * Compute the number of extra joint points required based on the material options
 * @param shape the shape to analyze
 * @param material the material used for the shape
 */
export const getSegmentJointPoints = (shape: DrawingShape, material: MaterialModel): number => {
    const materialOptions = MATERIAL_OPTIONS.find((option) => material.description?.match(option.nameMatcher));
    if (!shape || shape.skeleton.length === 0 || !materialOptions?.jointPointMaxLength) {
        return 0;
    }

    const segments = getSegmentsFromShape(shape);
    return segments.reduce(
        (joints, segment) =>
            joints +
            numberOfJointsPerLength(Math.max(...segment.dimensions), materialOptions.jointPointMaxLength as number),
        0
    );
};

/**
 * compute the number of joints for a given length of material and a maximum length before joints are required
 * @param edgeLength the length (or depth!) of the material
 * @param jointPoint the point at which a joint is required (i.e. every 97in)
 */
export const numberOfJointsPerLength = (edgeLength: number, jointPoint: number): number => {
    return jointPoint
        ? Math.floor(Math.abs(edgeLength) / jointPoint) -
              Number(roundToArrowLengthGranularity(Math.abs(edgeLength) % jointPoint) === 0)
        : 0; // subtract one to honor the god of Abraham but only if we're an exact multiple of the max
};

/**
 * compute the number of joints required for side items (backsplash and waterfalls) based on the length of the edge it's associated with
 * and/or the depth of the element
 * @param shape original shape
 * @param material the material being used
 */
export const getSideItemJointPoints = (shape: DrawingShape, material: MaterialModel): number => {
    const materialOptions = MATERIAL_OPTIONS.find((option) => material.description?.match(option.nameMatcher));
    if (!shape || shape.skeleton.length === 0 || !materialOptions || !materialOptions.jointPointMaxLength) {
        return 0;
    }

    return shape.drawingEdges
        .filter((edge) => edge.sideItem.type && edge.sideItem.depth)
        .reduce((acc, edge) => {
            const foundArrowInfo = shape?.arrowsInfo?.find(
                (arrowInfo) =>
                    arrowInfo?.drawingEdgeId === edge.id &&
                    !arrowInfo.isInterior &&
                    !arrowInfo.isBumpOut &&
                    !arrowInfo.isRadiusOut
            );
            if (foundArrowInfo && foundArrowInfo.size) {
                const edgeLength = Math.max(foundArrowInfo.size, edge.sideItem.depth);
                return acc + numberOfJointsPerLength(edgeLength, materialOptions.jointPointMaxLength as number);
            }
            return acc;
        }, 0);
};
